import {
  DotPulse,
  JellyTriangle,
  LineSpinner,
  Ring,
  Waveform
} from 'ldrs/react';
import { useEffect, useState } from 'react';
import 'ldrs/react/DotPulse.css';
import 'ldrs/react/JellyTriangle.css';
import 'ldrs/react/LineSpinner.css';
import 'ldrs/react/Ring.css';
import 'ldrs/react/Waveform.css';
import logo from '@/assets/logo.png';
import logoDark from '@/assets/logo-dark.png';
import { normalizePublicAssetUrl } from '@/feature/website-builder/model/publicAssetUrl';
import './v1.css';
import {
  defaultHsdeskLoaderColor,
  defaultHsdeskLoaderVariant,
  normalizeHsdeskLoaderColor,
  normalizeHsdeskLoaderVariant,
  type HsdeskLoaderVariant
} from './config';

export {
  defaultHsdeskLoaderColor,
  defaultHsdeskLoaderVariant,
  hsdeskLoaderColorPresets,
  hsdeskLoaderVariantOptions,
  hsdeskLoaderVariantValues,
  normalizeHsdeskLoaderColor,
  normalizeHsdeskLoaderVariant
} from './config';
export type { HsdeskLoaderVariant } from './config';

interface DefaultLoaderProps {
  variant?: HsdeskLoaderVariant | string;
  color?: string;
  backgroundColor?: string;
  compact?: boolean;
  showLogo?: boolean;
  size?: number | string;
  brandLogoUrl?: string;
  brandName?: string;
  hideDefaultBrand?: boolean;
}

function renderLoader(
  variant: HsdeskLoaderVariant,
  size: number | string,
  color: string
) {
  if (variant === 'ring') {
    return <Ring size={size} speed="2" stroke="5" color={color} />;
  }

  if (variant === 'dots') {
    return <DotPulse size={size} speed="1.25" color={color} />;
  }

  if (variant === 'line') {
    return <LineSpinner size={size} speed="1.8" stroke="5" color={color} />;
  }

  if (variant === 'wave') {
    return <Waveform size={size} speed="1" stroke="4" color={color} />;
  }

  return <JellyTriangle size={size} speed="2.50" color={color} />;
}

export const DefaultLoader = ({
  variant = defaultHsdeskLoaderVariant,
  color = defaultHsdeskLoaderColor,
  backgroundColor,
  compact = false,
  showLogo = !compact,
  size,
  brandLogoUrl,
  brandName,
  hideDefaultBrand = false
}: DefaultLoaderProps) => {
  const normalizedVariant = normalizeHsdeskLoaderVariant(variant);
  const normalizedColor = normalizeHsdeskLoaderColor(color);
  const loaderSize = size ?? (compact ? 48 : 84);
  const safeBrandLogoUrl = normalizePublicAssetUrl(brandLogoUrl);
  const [brandLogoFailed, setBrandLogoFailed] = useState(false);

  useEffect(() => setBrandLogoFailed(false), [safeBrandLogoUrl]);

  const showBrandLogo = Boolean(safeBrandLogoUrl && !brandLogoFailed);
  const accessibleBrandName = brandName?.trim() || 'HSDesk';
  const statusCopy = hideDefaultBrand
    ? 'Wczytywanie strony'
    : `Wczytywanie strony ${accessibleBrandName}`;

  return (
    <div
      className={`loader-container ${compact ? 'loader-container-compact' : ''}`}
      role="status"
      aria-live="polite"
      style={{
        color: normalizedColor,
        ...(backgroundColor ? { background: backgroundColor } : undefined)
      }}
    >
      {showLogo && (
        <div className="loader-brand" aria-label={accessibleBrandName}>
          {showBrandLogo ? (
            <img
              className="loader-brand-image loader-brand-image-company"
              src={safeBrandLogoUrl}
              alt={`Logo ${accessibleBrandName}`}
              onError={() => setBrandLogoFailed(true)}
            />
          ) : !hideDefaultBrand ? (
            <>
              <img
                className="loader-brand-image loader-brand-image-light"
                src={logo}
                alt="HSDesk"
              />
              <img
                className="loader-brand-image loader-brand-image-dark"
                src={logoDark}
                alt="HSDesk"
              />
            </>
          ) : null}
        </div>
      )}
      {renderLoader(normalizedVariant, loaderSize, normalizedColor)}
      <span className="loader-status-copy">{statusCopy}</span>
    </div>
  );
};
